/**
 * AccountClosure Component
 *
 * MVP 1.21.11 self-service deletion entry point. Supports:
 *  - Category-granular deletion (profile / booking_history / reviews /
 *    messages / consent_records / marketing_data — empty = all)
 *  - Password re-auth gate before the request is created
 *  - Status display + reclaim button while in the 30-day grace period
 *
 * Backend lifecycle: requested → processing → completed/failed/reclaimed.
 * Once a request moves past `requested`, cancellation is no longer allowed.
 *
 * Story: SCRUM-2264 (MVP 1.21.11.2). SCRUM-2228 created this tab; this story
 * replaces the single soft-delete button with the granular flow.
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Trash2, Lock, X, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import {
  requestAccountDeletion,
  getDeletionStatus,
  cancelAccountDeletion,
} from '../../services/authService';

// Full-account category options — kept for the closure path.
const CATEGORY_OPTIONS = [
  { key: 'profile', label: 'Profile information', description: 'Name, contact info, avatar' },
  { key: 'booking_history', label: 'Booking history', description: 'Past trips, receipts' },
  { key: 'reviews', label: 'Reviews', description: 'Reviews you have written' },
  { key: 'messages', label: 'Messages', description: 'Conversations with guides' },
  { key: 'consent_records', label: 'Consent records', description: 'Marketing & cookie preferences' },
  { key: 'marketing_data', label: 'Marketing data', description: 'Email lists, analytics segments' },
];

// SCRUM-2243 / MVP 1.21.11 §4.1 — granular per-category deletion. These
// categories purge without closing the account. `booking_history` is
// intentionally absent: it's under a 7yr legal-retention window, declined
// at the API and routed to the email channel.
const GRANULAR_CATEGORY_OPTIONS = [
  { key: 'marketing_analytics', label: 'Marketing & analytics data', description: 'Behavioral tracking, campaign attribution' },
  { key: 'saved_preferences', label: 'Saved preferences', description: 'Saved searches, favorites, filters' },
  { key: 'search_history', label: 'Search history', description: 'Past searches and recently viewed' },
];

function formatDate(value) {
  if (!value) {return '';}
  try {
    return new Date(value).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return String(value);
  }
}

function DeleteConfirmationModal({ isOpen, onClose, onConfirm, loading, error, selectedCategories }) {
  const [password, setPassword] = useState('');

  if (!isOpen) {return null;}

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(password);
  };

  const catLabels = selectedCategories.length === 0
    ? ['All account data']
    : selectedCategories.map((k) => CATEGORY_OPTIONS.find((c) => c.key === k)?.label || k);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800 leading-relaxed mb-2">
              You are requesting deletion of:
            </p>
            <ul className="text-sm text-red-800 list-disc list-inside">
              {catLabels.map((l) => <li key={l}>{l}</li>)}
            </ul>
            <p className="text-sm text-red-800 leading-relaxed mt-3">
              You have <strong>30 days</strong> to cancel before deletion becomes permanent.
              We will email you a reclaim link.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Lock className="w-4 h-4 inline mr-2" />
              Confirm with your password
              <span className="text-red-600 ml-1">*</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary"
              placeholder="Enter your password"
            />
            <p className="text-xs text-gray-500 mt-1">
              Signed in with Google/Facebook only? Leave this blank.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium ${
                loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              <Trash2 className="w-4 h-4" />
              {loading ? 'Submitting…' : 'Request deletion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ActiveRequestPanel({ request, onCancel, cancelling, cancelError }) {
  const canReclaim = request.status === 'requested';
  return (
    <div className="border-2 border-amber-300 rounded-lg bg-amber-50 p-6">
      <div className="flex items-start gap-4 mb-4">
        <div className="p-3 bg-amber-100 rounded-lg flex-shrink-0">
          <Clock className="w-6 h-6 text-amber-700" />
        </div>
        <div>
          <h4 className="text-base font-semibold text-amber-900 mb-2">
            Deletion request {request.status}
          </h4>
          <p className="text-sm text-amber-900 mb-2">
            Requested {formatDate(request.requestedAt)}.
            {canReclaim && (
              <> Your data will be deleted on <strong>{formatDate(request.gracePeriodEndsAt)}</strong> unless you cancel.</>
            )}
          </p>
          {request.categories && request.categories.length > 0 && (
            <p className="text-sm text-amber-900">
              Categories: {request.categories.join(', ')}
            </p>
          )}
        </div>
      </div>

      {cancelError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <p className="text-sm text-red-800">{cancelError}</p>
        </div>
      )}

      {canReclaim ? (
        <button
          onClick={onCancel}
          disabled={cancelling}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium ${
            cancelling
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-amber-600 text-white hover:bg-amber-700'
          }`}
        >
          <CheckCircle className="w-4 h-4" />
          {cancelling ? 'Cancelling…' : 'Cancel deletion'}
        </button>
      ) : (
        <p className="text-sm text-amber-900 italic">
          This request has moved past the grace period and can no longer be cancelled.
        </p>
      )}
    </div>
  );
}

function GranularDeletePanel({ onError }) {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const toggle = (key) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const handleGranular = async () => {
    try {
      setBusy(true);
      const data = await requestAccountDeletion(selected, '', 'granular');
      setDone({ ok: true, categories: data.categories || selected });
      setSelected([]);
    } catch (err) {
      if (err.status === 422 && err.data && err.data.reason) {
        const channel = err.data.emailChannel || 'privacy@outdoorguides.com';
        onError?.(`${err.message || 'Request declined.'} For a formal records request, email ${channel}.`);
      } else {
        onError?.(err.message || 'Failed to submit granular deletion.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-5 mb-6 bg-white">
      <h3 className="text-base font-semibold text-gray-900 mb-1">Delete specific data</h3>
      {/* SCRUM-2311 (SCRUM-2228 AC-04): the privacy@ address must be visible
          in the static render so a user can act on it without first triggering
          an error response that surfaces err.data.emailChannel. */}
      <p className="text-sm text-gray-600 mb-3">
        Remove data classes below without closing your account. Transaction
        history is retained per legal requirements — for formal records
        requests, email us at{' '}
        <a
          href="mailto:privacy@outdoorguides.com"
          className="text-blue-700 underline hover:text-blue-800"
        >
          privacy@outdoorguides.com
        </a>
        .
      </p>
      {done && (
        <div className="mb-3 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
          Deleted: {done.categories.join(', ')}. Your account is still active.
        </div>
      )}
      <div className="space-y-2 mb-3">
        {GRANULAR_CATEGORY_OPTIONS.map((opt) => (
          <label key={opt.key} className="flex items-start gap-3 cursor-pointer bg-gray-50 rounded p-2 border border-gray-200">
            <input
              type="checkbox"
              checked={selected.includes(opt.key)}
              onChange={() => toggle(opt.key)}
              className="mt-1 w-4 h-4 text-gray-700 border-gray-300 rounded"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
              <span className="block text-xs text-gray-500">{opt.description}</span>
            </span>
          </label>
        ))}
      </div>
      <button
        type="button"
        onClick={handleGranular}
        disabled={busy || selected.length === 0}
        className={`px-4 py-2 rounded-lg text-sm font-medium ${
          busy || selected.length === 0
            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
            : 'bg-gray-900 text-white hover:bg-gray-800'
        }`}
      >
        {busy ? 'Deleting…' : 'Delete selected'}
      </button>
    </div>
  );
}

export default function AccountClosure({ onAccountDeleted }) {
  const [activeRequest, setActiveRequest] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getDeletionStatus();
        if (!cancelled) {setActiveRequest(data);}
      } catch (err) {
        if (!cancelled) {setActiveRequest(null);}
      } finally {
        if (!cancelled) {setStatusLoading(false);}
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleCategory = (key) => {
    setSelectedCategories((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleRequestDeletion = async (password) => {
    try {
      setLoading(true);
      setError(null);
      const data = await requestAccountDeletion(selectedCategories, password, 'full');
      setShowModal(false);
      setActiveRequest({
        id: data.confirmationCode,
        status: data.status,
        categories: data.categories,
        requestedAt: new Date().toISOString(),
        gracePeriodEndsAt: data.gracePeriodEndsAt,
      });
      if (typeof onAccountDeleted === 'function') {
        onAccountDeleted();
      }
    } catch (err) {
      if (err.status === 401) {
        setError('Password is incorrect.');
      } else if (err.status === 422 && err.data && err.data.reason) {
        // SCRUM-2243: legal-hold synchronous decline.
        const channel = err.data.emailChannel || 'privacy@outdoorguides.com';
        setError(`${err.message || 'Request declined.'} For a formal records request, email ${channel}.`);
      } else {
        setError(err.message || 'Failed to submit deletion request.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    try {
      setCancelling(true);
      setCancelError(null);
      await cancelAccountDeletion();
      setActiveRequest(null);
      setConfirmed(false);
      setSelectedCategories([]);
    } catch (err) {
      setCancelError(err.message || 'Failed to cancel deletion request.');
    } finally {
      setCancelling(false);
    }
  };

  if (statusLoading) {
    return <div className="text-sm text-gray-600">Checking deletion status…</div>;
  }

  if (activeRequest) {
    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Account deletion</h3>
        <p className="text-sm text-gray-600 mb-6">
          You have an active deletion request.
        </p>
        <ActiveRequestPanel
          request={activeRequest}
          onCancel={handleCancelDeletion}
          cancelling={cancelling}
          cancelError={cancelError}
        />
      </div>
    );
  }

  return (
    <div>
      {/* SCRUM-2243 / §4.1: granular delete keeps the account active. */}
      <GranularDeletePanel onError={setError} onSuccess={() => { /* no-op; account stays active */ }} />

      <h3 className="text-lg font-semibold text-red-900 mb-1">Delete account</h3>
      <p className="text-sm text-gray-600 mb-6">
        Choose what to delete. You have 30 days to cancel before deletion is permanent.
      </p>
      {error && !showModal && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="border-2 border-red-300 rounded-lg bg-red-50 p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-red-100 rounded-lg flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h4 className="text-base font-semibold text-red-900 mb-2">
              Granular deletion
            </h4>
            <p className="text-sm text-red-800 leading-relaxed mb-2">
              Select the data classes you want deleted. Leave all unchecked to
              delete <strong>all account data</strong>.
            </p>
            <p className="text-sm text-red-800 leading-relaxed">
              Deletion runs after a 30-day grace period. You can cancel during
              that window from this page or from the confirmation email.
            </p>
          </div>
        </div>

        <fieldset className="mb-6">
          <legend className="text-sm font-medium text-gray-700 mb-2">
            Data categories to delete
          </legend>
          <div className="space-y-2">
            {CATEGORY_OPTIONS.map((opt) => (
              <label key={opt.key} className="flex items-start gap-3 cursor-pointer bg-white rounded p-3 border border-gray-200">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(opt.key)}
                  onChange={() => toggleCategory(opt.key)}
                  className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
                  <span className="block text-xs text-gray-500">{opt.description}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mb-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
            <span className="text-sm text-gray-700">
              I understand my data will be permanently deleted after the 30-day grace period.
            </span>
          </label>
        </div>

        <button
          onClick={() => setShowModal(true)}
          disabled={!confirmed}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium ${
            !confirmed
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-red-600 text-white hover:bg-red-700'
          }`}
        >
          <Trash2 className="w-4 h-4" />
          Continue to confirmation
        </button>
      </div>

      <DeleteConfirmationModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setError(null); }}
        onConfirm={handleRequestDeletion}
        loading={loading}
        error={error}
        selectedCategories={selectedCategories}
      />
    </div>
  );
}
